import React from "react";
import { Text } from "../../atoms/text/text";
import styles from "./channel-search.module.css";
import type { ChannelSearchMatch } from "./match-items";
import { SuggestionRow } from "./suggestion-row";
import { comboboxOptionId } from "./use-combobox";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The suggestion listbox — SR5, SR6, SR9, AX11.
 *
 * ⚠ INDEX 0 IS SR5's "select all" ROW. The arrow walk, the option ids and
 * `activate()` all count from it, so a reader arrowing to the top of the list
 * lands on it and Enter adds every match. The suggestions are 1..n.
 *
 * ⚠ THE HEADINGS ARE `role="group"`. A `listbox` owns `option` and `group`
 * and nothing else, so the loose `<li role="presentation">` that used to carry
 * a heading was never a valid owned element of it (1.3.1). ⛔ The note that
 * stood here said a group would re-base `aria-posinset` inside it. It does
 * not: every position in this list is AUTHORED, and an authored position is
 * used verbatim whatever contains it. The numbering stays flat across the
 * headings, which is what tells two rows sharing a name and a number apart.
 *
 * ⚠ `<ul>` MAY ONLY CONTAIN `<li>`, so a group is an `<li role="group">`
 * holding its own `<ul role="presentation">`. The inner list has no semantics
 * to lose, and `display: contents` keeps its box out of the layout, so the
 * rows stack exactly as they did when they were flat.
 *
 * ⚠ THE SELECT-ALL ROW IS NOT ONE OF THE SUGGESTIONS. The live region counts
 * matches, so `aria-setsize` counts matches too and the action row declares a
 * set of ONE — a reader was otherwise told "3 suggestions available" and then
 * "Sports Mix 205, 3 of 4". ⛔ Dropping the pair from that row instead is NOT
 * the same thing: the browser then computes it from the DOM and "1 of 4" comes
 * straight back.
 */
export interface SuggestionListProps {
  listboxId: string;
  listRef: React.RefObject<HTMLUListElement>;
  matches: ChannelSearchMatch[];
  /** Every match, when `matches` is only the rows drawn. The live region says
   *  this number, so `aria-setsize` must agree with it. */
  matchCount?: number;
  /** ⛔ ids, never labels. */
  selectedIds: readonly string[];
  activeIndex: number;
  /** SR5 — the whole row, already composed as "{prefix} {typed text}". */
  selectAllLabel: string;
  /** SR5 — the heading a row with no `group` of its own sits under. */
  groupHeading: string;
  /**
   * SR2's typing hint or SR9's no-match line. Present means there are no
   * options to show and this is what the list says instead.
   */
  message?: string;
  /** Choose the option at this flat index. Internal to this folder. */
  onActivate: (index: number) => void;
}

function SuggestionListImpl({
  listboxId,
  listRef,
  matches,
  matchCount = matches.length,
  selectedIds,
  activeIndex,
  selectAllLabel,
  groupHeading,
  message,
  onActivate
}: SuggestionListProps) {
  /* ⛔ THE SUGGESTIONS, not the options. This is the number the live region
     says out loud, so it is the number every row has to agree with. */
  const setSize = matchCount;

  /* Consecutive matches under one heading. A heading that comes back later
     opens a SECOND group rather than reopening the first: the positions are
     flat, so a run is all a group has to be. */
  const groups: {
    heading: string;
    rows: { match: ChannelSearchMatch; index: number }[];
  }[] = [];
  matches.forEach((match, position) => {
    const heading = match.item.group?.trim() || groupHeading;
    const open = groups[groups.length - 1];
    if (!open || open.heading !== heading) {
      groups.push({
        heading,
        rows: []
      });
    }
    groups[groups.length - 1].rows.push({
      match,
      index: position + 1
    });
  });

  const rows = groups.map(group => {
    /* Keyed AND labelled on the first position as well as the name: headings
       can interleave in a caller's ordering, and the name alone would then
       repeat — which would also point two groups at one label. */
    const headingId = `${listboxId}-group-${group.rows[0].index}`;
    return (
      <li aria-labelledby={headingId} key={headingId} role="group">
        {/* An EYEBROW, which is what the panel draws (`I…;13638:3304`):
            15px / 700 / uppercase on 0.48px of tracking. The `eyebrow`
            variant carries all four, so none of them is restated in the
            stylesheet — 0.032em × 15px IS 0.48px. */}
        <Text
          as="span"
          className={styles.groupHeading}
          id={headingId}
          variant="eyebrow"
        >
          {group.heading}
        </Text>
        <ul className={styles.optionGroup} role="presentation">
          {group.rows.map(({ match, index }) => (
            <SuggestionRow
              active={index === activeIndex}
              badge={match.item.badge}
              iconSrc={match.item.iconSrc}
              id={comboboxOptionId(listboxId, index)}
              /* ⛔ The caller's opaque id — never the label. Two items sharing
                 a label AND a number are two rows with two keys. */
              key={match.item.id}
              label={match.item.label}
              matchLength={match.length}
              matchStart={match.start}
              onActivate={() => onActivate(index)}
              posInSet={index}
              secondary={match.item.secondary}
              selected={selectedIds.includes(match.item.id)}
              setSize={setSize}
            />
          ))}
        </ul>
      </li>
    );
  });

  /* SR2's hint and SR9's no-match line replace the options outright; there is
     never a message beside a result.

     ⚠ AND THE ROW IS `aria-hidden`, for the reason the heading above is NOT.
     Loose text is no more a valid owned element of a `listbox` than a loose
     heading was — but a heading NAMES something a reader needs, where this
     line is already said, verbatim, by the live region `search-status.tsx`
     renders. Hiding it drops a duplicate; it loses nothing. */
  const body =
    message ?
      <li aria-hidden="true" className={styles.message} role="presentation">
        <Text as="span" variant="body-3">
          {message}
        </Text>
      </li> :
      <>
        <li
          aria-posinset={1}
          aria-selected={false}
          /* A set of ONE, deliberately — see the header. This row is an
             action, not one of the suggestions the live region counts. */
          aria-setsize={1}
          className={`${styles.option} ${styles.selectAll}`}
          data-active={activeIndex === 0 || undefined}
          id={comboboxOptionId(listboxId, 0)}
          onClick={() => onActivate(0)}
          onMouseDown={event => event.preventDefault()}
          role="option"
        >
          <Text as="span" variant="body-3">
            {selectAllLabel}
          </Text>
        </li>
        {rows}
      </>;

  return (
    <ul
      aria-label={groupHeading}
      /* The one attribute that makes `aria-selected` mean CHOSEN rather than
         highlighted. Without it a screen reader reads the selected row as the
         single current option and AX11 is quietly wrong. */
      aria-multiselectable="true"
      className={styles.listbox}
      id={listboxId}
      ref={listRef}
      role="listbox"
    >
      {body}
    </ul>
  );
}
export const SuggestionList = withSafeRender(SuggestionListImpl, "SuggestionList");

export default SuggestionList;
